import { finalizedUpload } from './finalized-upload';
import { hasPinSession } from './pin-session';
import { sessionAuthorization } from './auth';
import { requiredValue } from './oem-display';
import type { OemCommand, OemKind } from '@sqi/shared-schemas';
import { customerProjection } from '@sqi/shared-schemas';
import { authClient, runtimeMode, type OemContext } from './auth';
export interface OemRecord {
  id: string;
  rootId: string;
  skuId: string | null;
  kind: OemKind;
  version: number;
  hash: string;
  state: string;
  content: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
}
export interface OemDecision {
  disposition?: string;
  recordId: string;
  action: string;
  criteria: string[];
  comment: string | null;
  createdAt: string;
}
export interface OemWorkspace {
  project: {
    id: string;
    label: string;
    brand: string;
  };
  skus: {
    id: string;
    label: string;
  }[];
  records: OemRecord[];
  decisions: OemDecision[];
  order: {
    id: string;
    confirmedAt: string;
    handover: Record<string, unknown>;
    summary?: {
      skus: {
        id: string;
        label: string;
        quantity: string;
        unitPrice: string | null;
        currency: string | null;
        specId: string;
      }[];
      versions: {
        id: string;
        kind: string;
        label: string;
        version: number;
        hash: string;
        skuId: string | null;
        sampleCode?: string | null;
        fileId?: string | null;
      }[];
      decisions: {
        recordId: string;
        action: string;
        criteria: string[];
        authorityId: string;
        authorityVersion: number;
        recordedAt: string;
      }[];
      delivery: string | null;
      terms: string | null;
    };
    frozenManifest?: unknown;
    paymentEvidence?: unknown;
  } | null;
}
export class OemApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}
export async function request(path: string, body?: unknown) {
  if (runtimeMode !== 'connected' || !authClient || !import.meta.env.VITE_API_URL)
    throw new OemApiError('configuration_unavailable', 503);
  const authorization = await sessionAuthorization().catch(() => {
    dispatchEvent(new Event('sqi-session-expired'));
    throw new OemApiError('authentication_required', 401);
  });
  let response: Response;
  try {
    response = await fetch(`${import.meta.env.VITE_API_URL}/v1/oem${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        Authorization: authorization,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? null : JSON.stringify(body),
      cache: 'no-store',
    });
  } catch (e) {
    if (e instanceof TypeError) throw new OemApiError('transport_unavailable', 503);
    throw e;
  }
  const result = (await response.json()) as {
    data?: unknown;
    code?: string;
  };
  if (!response.ok) {
    if (response.status === 401) dispatchEvent(new Event('sqi-session-expired'));
    throw new OemApiError(result.code ?? 'unavailable', response.status);
  }
  return result.data;
}
function fixtureKey(context: OemContext) {
  return `sqi-oem-fixture-v3:${context.projectId}`;
}
async function fixture(context: OemContext): Promise<OemWorkspace> {
  const saved = localStorage.getItem(fixtureKey(context));
  if (saved) return JSON.parse(saved) as OemWorkspace;
  const { creamFixture } = await import('../../../../tests/fixtures/oem-cream');
  const ws = structuredClone(creamFixture);
  localStorage.setItem(fixtureKey(context), JSON.stringify(ws));
  return ws;
}
export async function loadWorkspace(context: OemContext): Promise<OemWorkspace> {
  if (runtimeMode === 'fixture') {
    const ws = await fixture(context);
    const bundle = (kind: string) =>
      [
        'material',
        'material_spec',
        'ingredient',
        'formula',
        'process',
        'density',
        'feasibility',
        'applicability',
        'test_plan',
        'test_result',
        'review',
        'hold',
      ].includes(kind)
        ? 'technical'
        : ['sourcing', 'cost', 'policy', 'exception'].includes(kind)
          ? 'cost'
          : [
                'component',
                'component_spec',
                'packaging',
                'artwork',
                'annotation',
                'commitment',
              ].includes(kind)
            ? 'design'
            : ['quote', 'po', 'order', 'handover'].includes(kind)
              ? 'commercial'
              : ['receipt', 'allocation'].includes(kind)
                ? 'finance'
                : 'shared';
    const records = ws.records.filter(
      (r) =>
        context.readBundles.includes(bundle(r.kind)) &&
        (context.role !== 'production' ||
          r.kind === 'feasibility' ||
          (Array.isArray(ws.order?.frozenManifest) &&
            ws.order.frozenManifest.some((ref: { id: string }) => ref.id === r.id))),
    );
    return {
      ...ws,
      records:
        context.audience === 'customer'
          ? records.map((r) => ({ ...r, content: customerProjection(r.content) }))
          : records,
      decisions: ws.decisions.filter((d) => records.some((r) => r.id === d.recordId)),
    };
  }
  if (hasPinSession()) return (await request(`/projects/${context.projectId}`)) as OemWorkspace;
  const { data } = await requiredValue(authClient).auth.getSession();
  if (!data.session) throw new OemApiError('authentication_required', 401);
  const offline = await import('./oem-offline');
  if (!navigator.onLine)
    return (await offline.cachedWorkspace(context, data.session.user.id)).workspace;
  let workspace: OemWorkspace;
  try {
    workspace = (await request(`/projects/${context.projectId}`)) as OemWorkspace;
  } catch (e) {
    if (e instanceof OemApiError && e.code === 'transport_unavailable') {
      const cached = await offline.cachedWorkspace(context, data.session.user.id);
      dispatchEvent(new Event('sqi-transport-offline'));
      return cached.workspace;
    }
    throw e;
  }
  await offline.retainWorkspace(context, data.session.user.id, workspace);
  return workspace;
}
export async function oemCommand(
  action: string,
  body: OemCommand,
  context: OemContext,
): Promise<Record<string, unknown>> {
  if (!navigator.onLine) throw new OemApiError('online_required', 503);
  if (runtimeMode === 'fixture') {
    const ws = await fixture(context);
    const receipts = JSON.parse(localStorage.getItem('sqi-oem-fixture-receipts') ?? '{}') as Record<
      string,
      {
        payload: string;
        outcome: Record<string, unknown>;
      }
    >;
    const key = context.projectId + ':' + body.idempotencyKey;
    const encoded = JSON.stringify({ action, body });
    if (receipts[key]) {
      if (receipts[key].payload !== encoded) throw new OemApiError('idempotency_conflict', 409);
      return { ...receipts[key].outcome, replayed: true };
    }
    if (!context.actions.includes(action)) throw new OemApiError('scope_denied', 403);
    let outcome: Record<string, unknown>;
    if (action === 'create' || action === 'save_draft' || action === 'revise') {
      const previous = ws.records.find((r) => r.id === body.target?.id);
      const content = body.payload.content as Record<string, unknown>;
      const hash = Array.from(
        new Uint8Array(
          await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(content))),
        ),
      )
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('');
      const id = crypto.randomUUID();
      const r: OemRecord = {
        id,
        rootId: previous?.rootId ?? id,
        skuId: body.skuId,
        kind: (previous?.kind ?? body.payload.kind) as OemKind,
        version: (previous?.version ?? 0) + 1,
        hash,
        state: 'draft',
        content,
        createdBy: 'fixture-author',
        createdAt: new Date().toISOString(),
      };
      ws.records.push(r);
      outcome = { recordId: r.id, version: r.version, hash: r.hash, state: 'draft' };
    } else if (action === 'confirm_order') {
      throw new OemApiError('fixture_confirmation_requires_connected_evidence', 409);
    } else {
      const r = ws.records.find((r) => r.id === body.target?.id);
      if (!r || r.hash !== body.target?.hash) throw new OemApiError('stale_target', 409);
      ws.decisions.push({
        recordId: r.id,
        action,
        criteria: (body.payload.criteria as string[]) ?? [],
        comment: (body.payload.comment as string) ?? null,
        createdAt: new Date().toISOString(),
      });
      outcome = { recordId: r.id, state: 'recorded', action };
    }
    outcome = { ...outcome, receiptId: crypto.randomUUID(), fixture: true, replayed: false };
    receipts[key] = { payload: encoded, outcome };
    localStorage.setItem('sqi-oem-fixture-receipts', JSON.stringify(receipts));
    localStorage.setItem(fixtureKey(context), JSON.stringify(ws));
    return outcome;
  }
  return (await request(`/commands/${action}`, body)) as Record<string, unknown>;
}
export async function recoverReceipt(context: OemContext, mutationId: string) {
  return request(`/receipts/${context.projectId}/${mutationId}`);
}
export interface HandoverAcknowledgements {
  orderId: string;
  projectId: string;
  orderVersion: 1;
  orderHash: string;
  entries: {
    id: string;
    actorId: string;
    comment: string;
    recordedAt: string;
    receiptId: string;
  }[];
}
export const loadHandoverAcknowledgements = (orderId: string) =>
  request(`/orders/${orderId}/acknowledgements`) as Promise<HandoverAcknowledgements>;
export const acknowledgeHandover = (body: {
  projectId: string;
  orderId: string;
  orderVersion: 1;
  orderHash: string;
  idempotencyKey: string;
  comment: string;
}) =>
  request(`/orders/${body.orderId}/acknowledgements`, body) as Promise<{
    acknowledgementId: string;
    orderId: string;
    actorId: string;
    receiptId: string;
    replayed: boolean;
  }>;
export const contextInventory = () =>
  request('/contexts') as Promise<{
    customers: {
      id: string;
      organizationId: string;
      label: string;
    }[];
    brands: {
      id: string;
      customerId: string;
      organizationId: string;
      label: string;
    }[];
  }>;
export const createContext = (action: string, body: unknown) =>
  request(`/contexts/${action}`, body) as Promise<{
    id: string;
    skuIds: string[];
    receiptId: string;
  }>;
export const inviteCustomer = (body: unknown) => request('/invitations', body);
export const acceptInvitation = (token: string) => request('/invitations/accept', { token });
export const revokeAuthority = (projectId: string, authorityId: string) =>
  request('/authorities/revoke', { projectId, authorityId });
export const projectNotifications = (projectId: string) =>
  request(`/notifications/${projectId}`) as Promise<
    {
      id: string;
      label: string;
      receiptId: string;
      createdAt: string;
    }[]
  >;
export const searchProject = (projectId: string, q: string) =>
  request(`/search/${projectId}?q=${encodeURIComponent(q)}`);
export interface OemFile {
  id: string;
  recordId: string;
  mime: string;
  sha256: string;
  bytes: number;
  state: string;
  bundle: string;
}
export const listFiles = (projectId: string) =>
  request(`/files/project/${projectId}`) as Promise<OemFile[]>;
export const reviewFile = (fileId: string, state: 'reviewed' | 'rejected', source: string) =>
  request(`/files/${fileId}/review`, { state, source });
async function fileRequest(path: string, init?: RequestInit) {
  if (runtimeMode !== 'connected' || !authClient || !import.meta.env.VITE_API_URL)
    throw new OemApiError('configuration_unavailable', 503);
  const authorization = await sessionAuthorization().catch(() => {
    dispatchEvent(new Event('sqi-session-expired'));
    throw new OemApiError('authentication_required', 401);
  });
  const headers = new Headers(init?.headers);
  headers.set('Authorization', authorization);
  const r = await fetch(`${import.meta.env.VITE_API_URL}/v1/oem/files/${path}`, {
    ...init,
    headers,
    cache: 'no-store',
  });
  if (!r.ok) throw new OemApiError('file_unavailable', r.status);
  return r;
}
export async function fileBlob(id: string) {
  return (await fileRequest(id)).blob();
}
export async function uploadFile(
  projectId: string,
  recordId: string,
  bundle: string,
  file: File,
  source: string,
) {
  const r = await fileRequest(`${projectId}/${recordId}`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type,
      'x-file-bundle': bundle,
      'x-file-source': encodeURIComponent(source),
    },
    body: file,
  });
  const response: unknown = await r.json();
  return finalizedUpload(
    response && typeof response === 'object' && 'data' in response ? response.data : undefined,
  );
}
export const prepareExport = (projectId: string, proposalId: string, idempotencyKey: string) =>
  request('/exports', { projectId, proposalId, idempotencyKey }) as Promise<{
    id: string;
    hash: string;
    snapshot: unknown;
    replayed: boolean;
  }>;
export interface AuthorityInventory {
  authorities: {
    id: string;
    userId: string;
    audience: string;
    role: string;
    actions: string[];
    readBundles: string[];
    version: number;
    evidence: string;
    validUntil: string;
    revokedAt: string | null;
  }[];
  members: {
    userId: string;
    role: string;
  }[];
  invitations: {
    id: string;
    userId: string;
    role: string;
    expiresAt: string;
    acceptedAt: string | null;
    revokedAt: string | null;
  }[];
  events: {
    id: string;
    authorityId: string;
    event: string;
    createdAt: string;
  }[];
}
export const authorityInventory = (projectId: string) =>
  request(`/authorities/${projectId}`) as Promise<AuthorityInventory>;
export const assignStaff = (body: unknown) => request('/staff', body);
export interface ImportInventory {
  batches: {
    id: string;
    artifactId: string;
    sourceVersion: string;
    sourceHash: string;
    bytes: number;
    transformationVersion: string;
    reviewed: boolean;
    adoptedRows: number;
    rows: {
      id: string;
      sheet: string;
      rowId: string;
      sourceIdentity: string;
      canonicalId: string | null;
      disposition: string;
      reason: string;
      normalized: Record<string, string>;
    }[];
  }[];
}
export const importInventory = (projectId: string) =>
  request(`/imports/${projectId}`) as Promise<ImportInventory>;
export const importCommand = (action: 'stage' | 'review' | 'adopt', body: unknown) =>
  request(`/imports/${action}`, body) as Promise<{
    batchId: string;
    receiptId: string;
    state: string;
  }>;

export interface CustomerAccountRequest {
  projectId: string;
  idempotencyKey: string;
  name: string;
  username: string;
  initialPassword: string;
  role: string;
  evidence: string;
  expiresAt: string;
}
export async function createCustomerAccount(body: CustomerAccountRequest) {
  return (await request('/customer-accounts', body)) as {
    operationId: string;
    username: string;
    expiresAt: string;
  };
}
export async function customerAccountSetup(operationId: string, accept = false, password?: string) {
  return (await request('/customer-accounts/setup', {
    operationId,
    accept,
    ...(password ? { password } : {}),
  })) as {
    projectId: string;
    label: string;
    role: string;
    expiresAt: string;
    completed?: boolean;
  };
}
